import { motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { Brain, Timer, RotateCcw, Award } from 'lucide-react'

const MentalGame = () => {
  const [gameState, setGameState] = useState('idle') // idle, playing, finished
  const [score, setScore] = useState(0)
  const [timeLeft, setTimeLeft] = useState(30)
  const [currentSequence, setCurrentSequence] = useState([])
  const [playerSequence, setPlayerSequence] = useState([])
  const [level, setLevel] = useState(1)
  const [message, setMessage] = useState('')
  const timerRef = useRef(null)

  // Générer une séquence aléatoire
  const generateSequence = (length) => {
    return Array.from({ length }, () => Math.floor(Math.random() * 9) + 1)
  }

  // Démarrer le jeu
  const startGame = () => {
    setGameState('playing')
    setScore(0)
    setTimeLeft(30)
    setLevel(1)
    setMessage('Mémorisez les chiffres !')
    const initialSequence = generateSequence(3)
    setCurrentSequence(initialSequence)
    setPlayerSequence([])
    
    // Montrer la séquence puis la cacher
    setTimeout(() => {
      setMessage('Maintenant, reproduisez la séquence !')
    }, 3000)

    // Démarrer le timer
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          endGame()
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  // Gérer le clic sur un chiffre
  const handleNumberClick = (number) => {
    if (gameState !== 'playing') return

    const newPlayerSequence = [...playerSequence, number]
    setPlayerSequence(newPlayerSequence)

    // Vérifier si la séquence est correcte
    if (newPlayerSequence.join('') === currentSequence.slice(0, newPlayerSequence.length).join('')) {
      if (newPlayerSequence.length === currentSequence.length) {
        // Séquence complète correcte
        const newScore = score + (level * 10)
        setScore(newScore)
        setMessage('Bravo ! Niveau suivant !')
        
        setTimeout(() => {
          const nextLevel = level + 1
          setLevel(nextLevel)
          const nextSequence = generateSequence(3 + nextLevel - 1)
          setCurrentSequence(nextSequence)
          setPlayerSequence([])
          setMessage('Mémorisez les nouveaux chiffres !')
          
          setTimeout(() => {
            setMessage('Maintenant, reproduisez la séquence !')
          }, 3000)
        }, 1500)
      }
    } else {
      // Mauvaise séquence
      setMessage('Erreur ! Essayez encore.')
      setTimeout(() => {
        setPlayerSequence([])
        setMessage('Reproduisez la séquence !')
      }, 1000)
    }
  }

  // Terminer le jeu
  const endGame = () => {
    clearInterval(timerRef.current)
    setGameState('finished')
    setMessage(`Jeu terminé ! Score final : ${score}`)
  }

  // Nettoyer l'intervalle
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  return (
    <section id="game" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center items-center gap-3 mb-4">
            <Brain className="w-8 h-8 text-primary" />
            <h2 className="section-title">Entraînement Mental</h2>
          </div>
          <p className="section-subtitle max-w-2xl mx-auto">
            Testez votre mémoire et votre concentration avec ce jeu de séquence numérique
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Carte du jeu */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {/* En-tête du jeu */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Timer className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">Temps</span>
                </div>
                <div className="text-2xl font-bold text-blue-700">{timeLeft}s</div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-600">Score</span>
                </div>
                <div className="text-2xl font-bold text-green-700">{score}</div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-sm font-medium text-purple-600 mb-1">Niveau</div>
                <div className="text-2xl font-bold text-purple-700">{level}</div>
              </div>

              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-sm font-medium text-orange-600 mb-1">Longueur</div>
                <div className="text-2xl font-bold text-orange-700">{currentSequence.length}</div>
              </div>
            </div>

            {/* Zone de jeu */}
            <div className="text-center mb-8">
              <div className={`text-lg font-semibold mb-6 min-h-[24px] ${
                message.includes('Bravo') ? 'text-green-600' : 
                message.includes('Erreur') ? 'text-red-600' : 'text-gray-700'
              }`}>
                {message}
              </div>

              {/* Séquence à mémoriser */}
              {gameState === 'playing' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-8"
                >
                  <div className="text-sm text-gray-600 mb-4">Séquence à mémoriser :</div>
                  <div className="flex justify-center gap-3 flex-wrap">
                    {currentSequence.map((num, index) => (
                      <motion.div
                        key={index}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.2 }}
                        className="w-12 h-12 bg-primary text-white rounded-lg flex items-center justify-center text-xl font-bold shadow-md"
                      >
                        {num}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Grille numérique */}
              <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-8">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <motion.button
                    key={num}
                    whileHover={{ scale: gameState === 'playing' ? 1.1 : 1 }}
                    whileTap={{ scale: gameState === 'playing' ? 0.9 : 1 }}
                    onClick={() => handleNumberClick(num)}
                    disabled={gameState !== 'playing'}
                    className={`w-16 h-16 rounded-xl text-xl font-bold transition-all ${
                      gameState === 'playing'
                        ? 'bg-gradient-to-br from-primary to-purple-600 text-white shadow-lg hover:shadow-xl cursor-pointer'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {num}
                  </motion.button>
                ))}
              </div>

              {/* Séquence du joueur */}
              {playerSequence.length > 0 && (
                <div className="mb-6">
                  <div className="text-sm text-gray-600 mb-3">Votre séquence :</div>
                  <div className="flex justify-center gap-2 flex-wrap">
                    {playerSequence.map((num, index) => (
                      <div
                        key={index}
                        className="w-10 h-10 bg-green-500 text-white rounded-lg flex items-center justify-center font-bold text-lg"
                      >
                        {num}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Boutons de contrôle */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {gameState === 'idle' || gameState === 'finished' ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={startGame}
                  className="btn-primary flex items-center justify-center gap-2 py-3 px-8"
                >
                  <Brain className="w-5 h-5" />
                  {gameState === 'finished' ? 'Rejouer' : 'Commencer le jeu'}
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={startGame}
                  className="btn-secondary flex items-center justify-center gap-2 py-3 px-8"
                >
                  <RotateCcw className="w-5 h-5" />
                  Redémarrer
                </motion.button>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">Comment jouer :</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Mémorisez la séquence de chiffres affichée</li>
                <li>• Reproduisez la même séquence en cliquant sur les chiffres</li>
                <li>• Chaque niveau réussi augmente la longueur de la séquence</li>
                <li>• Scorez le maximum de points avant la fin du temps !</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default MentalGame